import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import { formatCurrency } from '../../utils/payrollCalculations';

interface PayslipDetail {
  id: number;
  periodId: number;
  periodName: string;
  startDate: string;
  endDate: string;
  // Employee Details
  employeeName: string;
  employeeEmail: string;
  teacherName: string;
  teacherEmail: string;
  nrcNumber?: string;
  jobTitle?: string;
  dateEmployed?: string;
  bankName?: string;
  bankAccountNumber?: string;
  // Earnings
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
  grossSalary: number;
  // Deductions
  employeeNapsa?: number;
  employerNapsa?: number;
  daysAbsentDeduction: number;
  advanceSalaryRepayment: number;
  loanDeduction: number;
  napsaDeduction: number;
  payeDeduction: number;
  employeeNhima?: number;
  employerNhima?: number;
  nhimaDeduction: number;
  personalLevyDeduction: number;
  rentDeduction: number;
  schoolFeesDeduction: number;
  otherDeductions: number;
  totalDeductions: number;
  // Net
  netSalary: number;
  // Leave Info
  leaveDaysTaken: number;
  leaveDaysRemaining?: number;
  // Status
  status: string;
  paymentDate?: string;
  createdAt: string;
}

interface SchoolInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
}

const DEFAULT_SCHOOL_INFO: SchoolInfo = {
  name: 'School Name',
  address: 'Address not available',
  phone: 'Phone not available',
  email: 'Email not available'
};

const MyPayslipDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [payslip, setPayslip] = useState<PayslipDetail | null>(null);
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const role = (localStorage.getItem('role') || '').toLowerCase();
  const backPath = role === 'admin' || role === 'superadmin' ? '/payroll-management' : '/my-payroll';

  useEffect(() => {
    fetchPayslipDetail();
    fetchSchoolInfo();
  }, [id]);

  const fetchPayslipDetail = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(getApiUrl(`payroll/payslips/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || {};
      const normalized: PayslipDetail = {
        id: Number(data.id || 0),
        periodId: Number(data.periodId || 0),
        periodName: data.periodName || 'Payroll Period',
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        employeeName: data.teacherName || data.employeeName || data.name || 'Employee',
        employeeEmail: data.teacherEmail || data.employeeEmail || data.email || '',
        teacherName: data.teacherName || data.employeeName || data.name || 'Employee',
        teacherEmail: data.teacherEmail || data.employeeEmail || data.email || '',
        nrcNumber: data.nrcNumber || '',
        jobTitle: data.jobTitle || '',
        dateEmployed: data.dateEmployed || '',
        bankName: data.bankName || '',
        bankAccountNumber: data.bankAccountNumber || '',
        baseSalary: Number(data.baseSalary || 0),
        housingAllowance: Number(data.housingAllowance || 0),
        transportAllowance: Number(data.transportAllowance || 0),
        responsibilityAllowance: Number(data.responsibilityAllowance || 0),
        otherAllowances: Number(data.otherAllowances || 0),
        grossSalary: Number(data.grossSalary || 0),
        employeeNapsa: Number(data.employeeNapsa ?? data.napsaDeduction ?? 0),
        employerNapsa: Number(data.employerNapsa || 0),
        daysAbsentDeduction: Number(data.daysAbsentDeduction || 0),
        advanceSalaryRepayment: Number(data.advanceSalaryRepayment ?? data.advanceSalaryDeduction ?? 0),
        loanDeduction: Number(data.loanDeduction || 0),
        napsaDeduction: Number(data.napsaDeduction || 0),
        payeDeduction: Number(data.payeDeduction || 0),
        employeeNhima: Number(data.employeeNhima ?? data.nhimaDeduction ?? 0),
        employerNhima: Number(data.employerNhima || 0),
        nhimaDeduction: Number(data.nhimaDeduction || 0),
        personalLevyDeduction: Number(data.personalLevyDeduction || 0),
        rentDeduction: Number(data.rentDeduction || 0),
        schoolFeesDeduction: Number(data.schoolFeesDeduction || 0),
        otherDeductions: Number(data.otherDeductions || 0),
        totalDeductions: Number(data.totalDeductions || 0),
        netSalary: Number(data.netSalary || 0),
        leaveDaysTaken: Number(data.leaveDaysTaken || 0),
        leaveDaysRemaining: data.leaveDaysRemaining,
        status: data.status || 'generated',
        paymentDate: data.paymentDate || '',
        createdAt: data.createdAt || ''
      };
      setPayslip(normalized);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching payslip details');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchoolInfo = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(getApiUrl('school/details'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data || {};
      setSchoolInfo({
        name: data.name || DEFAULT_SCHOOL_INFO.name,
        address: data.address || DEFAULT_SCHOOL_INFO.address,
        phone: data.phone || DEFAULT_SCHOOL_INFO.phone,
        email: data.email || DEFAULT_SCHOOL_INFO.email
      });
    } catch {
      setSchoolInfo(DEFAULT_SCHOOL_INFO);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    if (!id) return;
    try {
      const token = localStorage.getItem('token') || '';
      const response = await axios.get(getApiUrl(`payroll/payslips/${id}/pdf`), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `payslip_${payslip?.employeeName?.replace(/\s+/g, '_') || 'employee'}_${payslip?.periodName?.replace(/\s+/g, '_') || 'period'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('PDF Download Error:', err);
      alert('Error downloading PDF. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZM', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZM');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payslip...</p>
        </div>
      </div>
    );
  }

  if (error || !payslip) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || 'Payslip not found'}
        </div>
        <button
          onClick={() => navigate(backPath)}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          ← Back to Payroll
        </button>
      </div>
    );
  }

  const normalizedStatus = payslip.status || 'generated';
  const statusLabel = normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1);

  const earnings = [
    { label: 'Basic Salary', value: payslip.baseSalary },
    { label: 'Housing Allowance', value: payslip.housingAllowance },
    { label: 'Transport Allowance', value: payslip.transportAllowance },
    { label: 'Responsibility Allowance', value: payslip.responsibilityAllowance },
    { label: 'Other Allowances', value: payslip.otherAllowances },
  ];

  const deductions = [
    { label: 'Days Absent Deduction', value: payslip.daysAbsentDeduction },
    { label: 'Advance Salary Repayment', value: payslip.advanceSalaryRepayment },
    { label: 'Loan Repayment', value: payslip.loanDeduction },
    { label: 'Employee NAPSA', value: payslip.employeeNapsa ?? payslip.napsaDeduction },
    { label: 'PAYE', value: payslip.payeDeduction },
    { label: 'Employee NHIMA', value: payslip.employeeNhima ?? payslip.nhimaDeduction },
    { label: 'Personal Levy', value: payslip.personalLevyDeduction },
    { label: 'Rent', value: payslip.rentDeduction },
    { label: 'School Fees', value: payslip.schoolFeesDeduction },
    { label: 'Other Deductions', value: payslip.otherDeductions },
  ].filter(d => d.value > 0);

  const employerContributions = [
    { label: 'Employer NAPSA', value: payslip.employerNapsa || 0 },
    { label: 'Employer NHIMA', value: payslip.employerNhima || 0 }
  ].filter((item) => item.value > 0);

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-6 print:hidden">
        <button
          onClick={() => navigate(backPath)}
          className="flex items-center text-gray-600 hover:text-gray-800"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Payroll
        </button>
        <div className="flex gap-3">
          <button
            onClick={handleDownloadPDF}
            className="flex items-center bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download PDF
          </button>
          <button
            onClick={handlePrint}
            className="flex items-center bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print Payslip
          </button>
        </div>
      </div>

      {/* Payslip Container */}
      <div className="bg-white shadow-lg max-w-4xl mx-auto print:shadow-none print:max-w-none">
        {/* Payslip Header */}
        <div className="bg-blue-900 text-white p-6 print:bg-blue-900">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold">{schoolInfo?.name || 'School Name'}</h1>
              <p className="mt-1 text-blue-200">{schoolInfo?.address}</p>
              <p className="text-blue-200">Tel: {schoolInfo?.phone} | Email: {schoolInfo?.email}</p>
            </div>
            <div className="text-right">
              <h2 className="text-xl font-semibold">PAYSLIP</h2>
              <p className="mt-1 text-blue-200">{payslip.periodName}</p>
            </div>
          </div>
        </div>

        {/* Employee Information */}
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Employee Information</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">Employee Name</p>
              <p className="font-medium">{payslip.employeeName}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">NRC Number</p>
              <p className="font-medium">{payslip.nrcNumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Job Title</p>
              <p className="font-medium">{payslip.jobTitle || 'Staff Member'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Date Employed</p>
              <p className="font-medium">{payslip.dateEmployed ? formatShortDate(payslip.dateEmployed) : 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Bank Name</p>
              <p className="font-medium">{payslip.bankName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Account Number</p>
              <p className="font-medium">{payslip.bankAccountNumber || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p className="font-medium">{payslip.employeeEmail}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Status</p>
              <span className={`px-2 py-1 rounded text-sm ${normalizedStatus === 'paid'
                ? 'bg-green-100 text-green-800'
                : normalizedStatus === 'approved'
                  ? 'bg-blue-100 text-blue-800'
                  : 'bg-yellow-100 text-yellow-800'
                }`}>
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* Earnings Section */}
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Earnings</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Description</th>
                  <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Amount (ZMW)</th>
                </tr>
              </thead>
              <tbody>
                {earnings.map((item, index) => (
                  <tr key={index} className={index === earnings.length - 1 ? 'font-bold bg-gray-50' : 'border-b'}>
                    <td className="py-3 px-3">{item.label}</td>
                    <td className="py-3 px-3 text-right">{formatCurrency(item.value)}</td>
                  </tr>
                ))}
                <tr className="font-bold bg-green-50">
                  <td className="py-3 px-3 text-green-800">Gross Income</td>
                  <td className="py-3 px-3 text-right text-green-800">{formatCurrency(payslip.grossSalary)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Deductions Section */}
        <div className="p-6 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Deductions</h3>
          {deductions.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Description</th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Amount (ZMW)</th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-3">{item.label}</td>
                      <td className="py-2 px-3 text-right text-red-600">({formatCurrency(item.value)})</td>
                    </tr>
                  ))}
                  <tr className="font-bold bg-red-50">
                    <td className="py-3 px-3 text-red-800">Total Deductions</td>
                    <td className="py-3 px-3 text-right text-red-800">({formatCurrency(payslip.totalDeductions)})</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No deductions recorded</p>
          )}
        </div>

        {employerContributions.length > 0 && (
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Employer Contributions</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-600">Description</th>
                    <th className="text-right py-2 px-3 text-sm font-medium text-gray-600">Amount (ZMW)</th>
                  </tr>
                </thead>
                <tbody>
                  {employerContributions.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 px-3">{item.label}</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(item.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Net Salary Section */}
        <div className="p-6">
          <div className="flex justify-end">
            <div className="w-full md:w-1/2">
              <div className="flex justify-between items-center py-3 border-b-2 border-gray-300">
                <span className="text-lg font-semibold text-gray-800">Net Pay</span>
                <span className="text-2xl font-bold text-green-600">{formatCurrency(payslip.netSalary)}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2 text-right">
                {payslip.netSalary > 0
                  ? `ZMW ${numberToWords(payslip.netSalary).toUpperCase()} ONLY`
                  : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Leave Information */}
        {(payslip.leaveDaysTaken > 0 || payslip.leaveDaysRemaining !== undefined) && (
          <div className="p-6 border-t bg-gray-50">
            <h4 className="font-semibold text-gray-800 mb-2">Leave Information</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500">Leave Days Taken</p>
                <p className="font-medium">{payslip.leaveDaysTaken} days</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Leave Days Remaining</p>
                <p className="font-medium">{payslip.leaveDaysRemaining ?? (30 - payslip.leaveDaysTaken)} days</p>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t bg-gray-100">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="text-sm text-gray-500">
              <p>Payslip ID: {payslip.id}</p>
              <p>Generated on: {formatDate(payslip.createdAt)}</p>
            </div>
            <div className="w-full max-w-xs text-sm text-gray-700">
              <div className="border-b border-gray-500 h-10" />
              <p className="mt-2 text-center font-medium">Employee Signature</p>
            </div>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body {
            background: white;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:max-w-none {
            max-width: none !important;
          }
          .bg-blue-900 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-green-50 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-red-50 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .bg-gray-50 {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </div>
  );
};
// Helper function to convert number to words (basic implementation)
function numberToWords(n: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const numToWords = (num: number): string => {
    if (num === 0) return 'Zero';

    let words = '';

    if (num >= 1000000) {
      words += numToWords(Math.floor(num / 1000000)) + ' Million ';
      num %= 1000000;
    }

    if (num >= 1000) {
      words += numToWords(Math.floor(num / 1000)) + ' Thousand ';
      num %= 1000;
    }

    if (num >= 100) {
      words += ones[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }

    if (num >= 20) {
      words += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    }

    if (num > 0) {
      words += ones[num] + ' ';
    }

    return words.trim();
  };

  const wholePart = Math.floor(n);
  const fractionalPart = Math.round((n - wholePart) * 100);

  let result = numToWords(wholePart);

  if (fractionalPart > 0) {
    result += ' Kwacha and ' + numToWords(fractionalPart) + ' Ngwee';
  } else {
    result += ' Kwacha';
  }

  return result;
}

export default MyPayslipDetail;



